import { Component, define } from '@theme/component'

/** @param {HTMLElement} element */
const isVisible = (element) => !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length)

/**
 * Play/pause and mute buttons (snippets/core-video-controls.liquid) for the
 * videos with the class in `video-class` anywhere on the page. With a desktop
 * and a mobile video it controls only the visible one; hidden ones are paused,
 * rewound and muted. Icons follow `data-playing` / `data-muted` on this
 * element (set once a video can play, so nothing shows before that).
 *
 * Boolean attributes:
 * - `scroll-autoplay`: play the videos while half visible, pause otherwise
 * - `play-on-click-video`: clicking a video toggles it (and unmutes on play)
 * - `hide-button-on-play`: hide the play button while playing
 * - `pause-page-videos-on-play`: pause every other video on the page first
 */
export class CoreVideoControls extends Component {
  /** @type {HTMLVideoElement[]} */
  #videos = []

  connectedCallback() {
    super.connectedCallback()
    const videoClass = this.getAttribute('video-class')?.trim()
    if (!videoClass) return
    const { signal } = this

    this.#videos = /** @type {HTMLVideoElement[]} */ (
      [...document.getElementsByClassName(videoClass)].filter((el) => el instanceof HTMLVideoElement)
    )
    this.#resetHidden()

    window.addEventListener(
      'resize',
      () => {
        this.#resetHidden()
        this.#sync()
      },
      { signal }
    )

    for (const video of this.#videos) {
      video.addEventListener('play', () => this.#showPlaying(true), { signal })
      video.addEventListener('pause', () => this.#showPlaying(false), { signal })
      // Refused autoplay may never load data: show the play button now, not on `canplay`
      video.addEventListener('video:autoplay-blocked', () => this.#sync(true), { signal })
      if (this.hasAttribute('play-on-click-video')) {
        video.addEventListener(
          'click',
          () => {
            if (video.paused) {
              this.#play(video)
              this.#setMuted(video, false)
            } else {
              this.#pause(video)
            }
          },
          { signal }
        )
      }
      if (video.readyState >= HTMLMediaElement.HAVE_METADATA) this.#sync()
      else video.addEventListener('canplay', () => this.#sync(), { once: true, signal })
    }

    if (this.hasAttribute('scroll-autoplay')) {
      const observer = new IntersectionObserver(
        (entries) => {
          for (const { target, isIntersecting } of entries) {
            if (!(target instanceof HTMLVideoElement)) continue
            if (isIntersecting) this.#play(target)
            else this.#pause(target)
          }
        },
        { threshold: 0.5 }
      )
      this.#videos.forEach((video) => observer.observe(video))
      signal.addEventListener('abort', () => observer.disconnect())
    }

    this.on('click', '.js-controls-play-button', () => {
      this.#resetHidden()
      for (const video of this.#visibleVideos) {
        if (video.paused) this.#play(video)
        else this.#pause(video)
      }
    })
    this.on('click', '.js-controls-mute-button', () => {
      this.#resetHidden()
      for (const video of this.#visibleVideos) this.#setMuted(video, !video.muted)
    })
  }

  get #visibleVideos() {
    return this.#videos.filter(isVisible)
  }

  /** Pauses, rewinds and mutes the videos hidden at this viewport size. */
  #resetHidden() {
    for (const video of this.#videos) {
      if (isVisible(video)) continue
      video.pause()
      video.currentTime = 0
      video.muted = true
    }
  }

  /** @param {HTMLVideoElement} video */
  #play(video) {
    if (this.hasAttribute('pause-page-videos-on-play')) {
      for (const other of document.querySelectorAll('video')) other.pause()
    }
    video.play().catch(() => undefined)
    this.#showPlaying(true)
  }

  /** @param {HTMLVideoElement} video */
  #pause(video) {
    video.pause()
    this.#showPlaying(false)
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {boolean} muted
   */
  #setMuted(video, muted) {
    video.muted = muted
    this.dataset.muted = String(muted)
  }

  /** @param {boolean} playing */
  #showPlaying(playing) {
    this.dataset.playing = String(playing)
    if (this.hasAttribute('hide-button-on-play')) {
      this.querySelector('.js-controls-play-button')?.classList.toggle(
        'khy-core-video-controls__controls__button--hidden',
        playing
      )
    }
  }

  /** Icons follow the visible video's state. @param {boolean} [force] Even before it can play */
  #sync(force = false) {
    for (const video of this.#visibleVideos) {
      if (force && !video.paused) continue
      this.#showPlaying(!video.paused)
      this.dataset.muted = String(video.muted)
    }
  }
}

define('core-video-controls', CoreVideoControls)
